/**
 * Nebenforderungen einer Mahnung: Mahngebuehr, Verzugspauschale, Verzugszinsen.
 *
 * Der Wert kommt automatisch aus dem Beleg (~45% der Mahnungen nennen ihn
 * woertlich) und ist hier von Hand korrigierbar. Sobald jemand editiert, gilt
 * `fees_source='manual'` und keine Automatik fasst das Feld mehr an — ein
 * „Neu berechnen"-Knopf stellt den automatischen Wert wieder her.
 *
 * Die rohe Differenz (Mahnbetrag − Summe der zugeordneten Rechnungen) wird nur
 * noch als Hinweis gezeigt: sie ist am Bestand unbrauchbar (33 von 66
 * verknuepften Mahnungen ergaben NEGATIVE „Gebuehren").
 */
import { useEffect, useState } from 'react';
import { Loader2, Pencil, RotateCcw, Save, X } from 'lucide-react';
import { invoices as invoicesApi } from '@/utils/api';
import { toast } from 'sonner';

const formatCurrency = (v: number | null | undefined) =>
  v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

const parseAmount = (s: string): number | null => {
  const t = String(s).trim().replace(/\./g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

interface Props {
  invoice: any;
  onChanged: () => void;
}

export default function ReminderFeesEditor({ invoice, onChanged }: Props) {
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dunning, setDunning] = useState('');
  const [penalty, setPenalty] = useState('');
  const [interest, setInterest] = useState('');
  const [total, setTotal] = useState('');

  const fmtInput = (v: number | null | undefined) =>
    v === null || v === undefined ? '' : String(v).replace('.', ',');

  useEffect(() => {
    setDunning(fmtInput(invoice.fee_dunning));
    setPenalty(fmtInput(invoice.fee_penalty));
    setInterest(fmtInput(invoice.fee_interest));
    setTotal(fmtInput(invoice.fees_amount));
    setEdit(false);
  }, [invoice.id, invoice.fees_amount, invoice.fee_dunning, invoice.fee_penalty, invoice.fee_interest]);

  const buckets = [parseAmount(dunning), parseAmount(penalty), parseAmount(interest)].filter(
    (v): v is number => v !== null,
  );
  const bucketSum = buckets.length > 0 ? Math.round(buckets.reduce((s, v) => s + v, 0) * 100) / 100 : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      // Sind Einzelposten gepflegt, gilt deren Summe — sonst der direkt
      // eingetragene Gesamtbetrag. Zwei Wahrheiten wuerden sonst driften.
      const payload: Record<string, any> = {
        fee_dunning: parseAmount(dunning),
        fee_penalty: parseAmount(penalty),
        fee_interest: parseAmount(interest),
        fees_amount: bucketSum !== null ? bucketSum : parseAmount(total),
      };
      await invoicesApi.update(invoice.id, payload);
      toast.success('Nebenforderung gespeichert');
      setEdit(false);
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalc = async () => {
    setSaving(true);
    try {
      const res = await invoicesApi.recalcFees(invoice.id);
      toast.success(
        res.data?.fees_amount === null
          ? 'Neu berechnet — aus dem Beleg nicht ermittelbar'
          : `Neu berechnet: ${formatCurrency(res.data.fees_amount)}`,
      );
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Neuberechnung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const isManual = invoice.fees_source === 'manual';
  const autoDiff = invoice.fees_auto_amount;

  const field = (label: string, value: string, setter: (v: string) => void) => (
    <label className="flex-1 min-w-[110px]">
      <span className="block text-[11px] text-gray-500 mb-1">{label}</span>
      <input
        value={value}
        onChange={(e) => setter(e.target.value)}
        placeholder="—"
        inputMode="decimal"
        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm text-right focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />
    </label>
  );

  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-800">Nebenforderungen</h4>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Mahngebühr, Verzugspauschale und Zinsen — gesondert von der Hauptforderung
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isManual && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
              manuell
            </span>
          )}
          {!edit && (
            <button
              onClick={() => setEdit(true)}
              className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-primary-600"
            >
              <Pencil className="w-3.5 h-3.5" /> Bearbeiten
            </button>
          )}
        </div>
      </div>

      {edit ? (
        <>
          <div className="flex flex-wrap gap-3">
            {field('Mahngebühr', dunning, setDunning)}
            {field('Verzugspauschale', penalty, setPenalty)}
            {field('Verzugszinsen', interest, setInterest)}
            {bucketSum === null && field('Gesamt', total, setTotal)}
          </div>
          {bucketSum !== null && (
            <p className="text-[11px] text-gray-500 mt-2">
              Gesamt aus den Einzelposten: <span className="font-medium">{formatCurrency(bucketSum)}</span>
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
            </button>
            <button
              onClick={() => setEdit(false)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              <X className="w-4 h-4" /> Abbrechen
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-semibold text-orange-800">
              {invoice.fees_amount === null || invoice.fees_amount === undefined
                ? 'nicht ermittelbar'
                : formatCurrency(invoice.fees_amount)}
            </span>
            {(invoice.fee_dunning || invoice.fee_penalty || invoice.fee_interest) && (
              <span className="text-xs text-gray-600">
                {invoice.fee_dunning ? `Mahngebühr ${formatCurrency(invoice.fee_dunning)}` : ''}
                {invoice.fee_penalty ? ` · Pauschale ${formatCurrency(invoice.fee_penalty)}` : ''}
                {invoice.fee_interest ? ` · Zinsen ${formatCurrency(invoice.fee_interest)}` : ''}
              </span>
            )}
          </div>

          {invoice.fees_amount === null && (
            <p className="text-xs text-gray-600 mt-1">
              Im Beleg war kein Gebührenbetrag zu finden — bitte von Hand nachtragen.
            </p>
          )}

          <div className="flex items-center gap-3 mt-3 text-[11px] text-gray-500">
            {autoDiff !== null && autoDiff !== undefined && (
              <span title="Mahnbetrag minus Summe der zugeordneten Rechnungen — nur ein Hinweis, oft unzuverlässig">
                Differenz zu den zugeordneten Rechnungen: {formatCurrency(autoDiff)}
              </span>
            )}
            <button
              onClick={handleRecalc}
              disabled={saving}
              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 disabled:opacity-50 ml-auto"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Neu berechnen
            </button>
          </div>
        </>
      )}
    </div>
  );
}
